import type { Purgeur } from '@stockflow/offline-core';
import { supprimerToutesLesBases } from '../db/base-locale';
import { droitsEnMemoire } from './session-coffre';

/**
 * CE QUE LA CAISSE GARDE SUR LE POSTE, ET QUI DOIT PARTIR AVEC SON PROPRIETAIRE.
 *
 * Declaratif : chaque entree dit ce qu'elle efface et pour quel perimetre.
 * Ajouter une donnee locale, c'est ajouter une ligne ici — l'oubli est le risque
 * d'un poste partage, et il ne se voit pas avant qu'un second utilisateur ne
 * tombe sur les donnees du premier.
 *
 * ── CE QUI N'EST VOLONTAIREMENT PAS ICI ──
 *
 *   stockflow.file-poussee       Les ventes non transmises. La file n'abandonne
 *                                JAMAIS : l'effacer perdrait des ventes.
 *   stockflow-jetons, coffre PIN La session et le coffre sont geres par
 *                                `AuthService` ; la preparation du poste
 *                                s'execute APRES l'ouverture de la session.
 *   selectedCompanyId, currentUser*   L'identite de la session qui vient de s'ouvrir.
 *   pos_accounts, theme, language     Preferences du poste, pas des donnees d'utilisateur.
 *   stockflow.terminal.code      L'identite du terminal, pas celle de la personne.
 */

const retirer = (...clefs: string[]) => () => {
  for (const c of clefs) localStorage.removeItem(c);
};

/** Retire toutes les clefs `localStorage` qui commencent par `prefixe`. */
const retirerParPrefixe = (prefixe: string) => () => {
  const aRetirer: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const clef = localStorage.key(i);
    if (clef?.startsWith(prefixe)) aRetirer.push(clef);
  }
  aRetirer.forEach((c) => localStorage.removeItem(c));
};

const BASE_PANIER = 'stockflow_pos';
const MAGASIN_PANIER = 'cart';
const PREFIXE_PANIER = 'current_cart_';

/**
 * Efface les paniers persistes de TOUTES les entreprises, et eux seuls.
 *
 * Le panier est range sous `current_cart_<entreprise>` — par entreprise, pas par
 * utilisateur : le caissier B de la meme entreprise rouvrait le panier de A. La
 * clef de chiffrement (`crypto_key`) est dans le meme magasin et n'est pas touchee.
 */
export function purgerPaniers(): Promise<void> {
  if (typeof indexedDB === 'undefined') return Promise.resolve();
  return new Promise((resolve, reject) => {
    const ouverture = indexedDB.open(BASE_PANIER, 1);
    ouverture.onupgradeneeded = () => {
      if (!ouverture.result.objectStoreNames.contains(MAGASIN_PANIER)) {
        ouverture.result.createObjectStore(MAGASIN_PANIER);
      }
    };
    ouverture.onerror = () => reject(ouverture.error);
    ouverture.onsuccess = () => {
      const bd = ouverture.result;
      const tx = bd.transaction(MAGASIN_PANIER, 'readwrite');
      const curseur = tx.objectStore(MAGASIN_PANIER).openKeyCursor();
      curseur.onsuccess = () => {
        const c = curseur.result;
        if (!c) return;
        if (String(c.key).startsWith(PREFIXE_PANIER)) tx.objectStore(MAGASIN_PANIER).delete(c.key);
        c.continue();
      };
      tx.oncomplete = () => {
        bd.close();
        resolve();
      };
      tx.onerror = () => {
        bd.close();
        reject(tx.error);
      };
    };
  });
}

/**
 * Efface les reponses d'API gardees par le service worker.
 *
 * `ngsw-config.json` met `/api/**` en cache une heure : paie, droits, messages,
 * hors de tout controle de role ou de deconnexion. On ne supprime QUE les caches
 * de donnees (`...:data:...`), jamais ceux de l'application, sans quoi le poste ne
 * pourrait plus demarrer hors ligne.
 *
 * Hypothese a verifier : les noms exacts des caches du worker Angular, a relever
 * dans un build de production (le worker est desactive en developpement).
 */
export async function purgerCachesApi(): Promise<void> {
  if (typeof caches === 'undefined') return;
  const noms = await caches.keys();
  await Promise.all(noms.filter((n) => n.startsWith('ngsw:') && n.includes(':data:')).map((n) => caches.delete(n)));
}

export const PURGEURS_VENTE: Purgeur[] = [
  {
    id: 'droits',
    perimetre: 'utilisateur',
    // La table en memoire, et d'anciennes copies EN CLAIR d'avant ce changement.
    purger: () => {
      droitsEnMemoire.vider();
      retirerParPrefixe('currentUserPermissions_')();
    },
  },
  { id: 'role', perimetre: 'utilisateur', purger: retirer('currentUserRole') },
  { id: 'profil', perimetre: 'utilisateur', purger: retirer('pos_user_profile') },
  { id: 'rh', perimetre: 'utilisateur', purger: retirer('pos_hr_leaves', 'pos_hr_clock', 'pos_hr_payslips') },
  { id: 'taches', perimetre: 'utilisateur', purger: retirer('pos_todos') },
  { id: 'calendrier', perimetre: 'utilisateur', purger: retirer('pos_calendar_events') },
  { id: 'paniers', perimetre: 'utilisateur', purger: purgerPaniers },
  { id: 'bases-locales', perimetre: 'utilisateur', purger: supprimerToutesLesBases },
  { id: 'cache-api', perimetre: 'utilisateur', purger: purgerCachesApi },
  { id: 'catalogue', perimetre: 'entreprise', purger: retirerParPrefixe('stockflow.catalogue.') },
  { id: 'entrepot', perimetre: 'entreprise', purger: retirer('pos_warehouse_id') },
  { id: 'mode-pos', perimetre: 'entreprise', purger: retirerParPrefixe('stockflow.mode-pos.') },
];
